import type { IFuelTypes, IUserProfile } from "@origin-digital/sales-common";
import {
  constructUserProfileFuelURLParams,
  getRequiredFuel,
  getServiceableFuel,
} from "@origin-digital/user-profile";
import {
  NMIResolutionState,
  ConnectionAddressObject,
} from "@origin-digital/sales-energy-address-lookup";
import { isBusinessPage } from "./pageContext";

const SALES_CUSTOMISATION_SERVICES = [
  "electricity",
  "gas",
  "internet",
  "lpg",
] as const;

export type SalesCustomisationServices = Partial<
  Record<(typeof SALES_CUSTOMISATION_SERVICES)[number], boolean>
>;

export const getAuthMovePath = ({ customerClass }: ConnectionAddressObject) => {
  return customerClass === "SME" ? "/my/business/move-home" : "/my/move-home";
};

export const getMoveParams = ({ originAddressId }: ConnectionAddressObject) => {
  const params = new URLSearchParams({
    campaignAppId: "movers-widget",
    journeyType: "Move",
    originAddressId: originAddressId!,
    planFetchStrategy: "RecommendedMove",
  });
  return params.toString();
};

export const getAuthMoveURL = (
  userProfile: IUserProfile,
  movingAddress: ConnectionAddressObject,
  movingAddressResolutionState: NMIResolutionState,
  useCart = false
) => {
  const movePath = getAuthMovePath(movingAddress);
  const moveParams = getMoveParams(movingAddress);
  const userParams = constructUserProfileFuelURLParams(
    userProfile,
    movingAddress,
    movingAddressResolutionState
  );
  return `${movePath}?${moveParams}&${userParams}&useCart=${useCart}`;
};

export const getUnauthMoveURL = (
  movingAddress: ConnectionAddressObject,
  movingAddressResolutionState: NMIResolutionState,
  useCart = false
) => {
  const moveParams = getMoveParams(movingAddress);

  const unauthParams = new URLSearchParams({
    fuel: getRequiredFuel(movingAddressResolutionState, movingAddress) ?? "",
    serviceableFuel: getServiceableFuel(movingAddress) ?? "",
    useCart: `${useCart}`,
  }).toString();

  return isBusinessPage()
    ? `/business/signup?${moveParams}&${unauthParams}`
    : `/sign-up?${moveParams}&${unauthParams}`;
};

export const getSalesCustomisationURL = (
  services: SalesCustomisationServices = {},
  fuel?: IFuelTypes
): string => {
  const params = new URLSearchParams();

  if (fuel) {
    params.set("fuel", fuel);
  }

  for (const service of SALES_CUSTOMISATION_SERVICES) {
    const isEnergyService = service === "electricity" || service === "gas";
    if ((!fuel || !isEnergyService) && services[service]) {
      params.set(service, "true");
    }
  }

  const queryString = params.toString();

  return queryString ? `/customisation?${queryString}` : "/customisation";
};

export const getMovesCrossSellURL = (
  originAddressId: string,
  fuel?: IFuelTypes,
  services: SalesCustomisationServices = {}
): string => {
  const params = new URLSearchParams({ originAddressId });
  if (fuel) params.set("fuel", fuel);
  for (const service of SALES_CUSTOMISATION_SERVICES) {
    const isEnergyService = service === "electricity" || service === "gas";
    if ((!fuel || !isEnergyService) && services[service]) {
      params.set(service, "true");
    }
  }
  return `/customisation/services?${params.toString()}`;
};
